import type { Guide } from './types.js';

/**
 * The end-to-end workflow document, written to be handed to USPS as a standalone
 * description of how repair work orders are raised, authorized, performed, and
 * closed. It is customer-facing: it explains that VGS prices work and that USPS
 * authorizes it, without describing VGS cost build-up.
 */
export const WORKFLOW_DOC: Guide = {
  title: 'Work Order Management workflow',
  subtitle:
    'End-to-end process for Reimbursable Variable Service and Repair work orders: intake, investigation, pricing, authorization, execution, completion, closeout, and reporting.',
  audience: 'Prepared by VGS for the United States Postal Service.',
  sections: [
    {
      id: 'purpose',
      title: '1. Purpose and scope',
      blocks: [
        {
          kind: 'paragraph',
          text: 'This document describes how a repair request is handled from the moment a Postal Service facility raises it to the moment the record is closed and reportable. It covers Reimbursable Variable Service and Repair work performed under the task order, and applies to every such work order without exception: there is no side channel by which work is performed or authorized outside the platform.',
        },
        {
          kind: 'paragraph',
          text: 'Three principles govern the process. Work is performed only after the Postal Service authorizes the price. Authority to authorize follows the dollar value of the work. Every action is recorded, attributed, and time-stamped in a record that cannot be edited afterwards.',
        },
      ],
    },
    {
      id: 'participants',
      title: '2. Participants',
      blocks: [
        {
          kind: 'definitions',
          items: [
            {
              term: 'Requestor (Postal Service)',
              description:
                'Reports the repair to VGS, provides site access and a point of contact, and follows progress. Requestors do not authorize price.',
            },
            {
              term: 'Contracting Officer’s Representative (COR)',
              description:
                'Authorizes proposals within the COR dollar limit for the facilities they cover, or refers them to the Contracting Officer. A facility may have more than one COR.',
            },
            {
              term: 'Contracting Officer (CO)',
              description:
                'Authorizes at any value permitted by the agreement, rules on requests to proceed with fewer than three quotes, may require competition on a specific work order, and exercises discretion where the Facilities Response Line declines the work.',
            },
            {
              term: 'VGS facility and program management',
              description:
                'Acknowledges the request, investigates, prices the repair, submits the proposal, schedules and performs the work, documents completion, and closes the work order.',
            },
            {
              term: 'FSCMC and audit users',
              description:
                'Read-only access to the live portal and its reporting for the facilities within their scope.',
            },
          ],
        },
        {
          kind: 'note',
          variant: 'info',
          title: 'Separation of duties',
          text: 'No VGS account holds approval authority in the platform. This is enforced in the permission data itself, not by interface design, so VGS cannot authorize the price it has proposed under any circumstances.',
        },
      ],
    },
    {
      id: 'intake',
      title: '3. Stage 1 — Intake',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The Postal Service reports the problem to VGS by telephone or email. VGS enters the work order, recording the facility, repair category, priority, the location of the fault, a description of the problem, and the site point of contact who reported it; photographs can be attached by either side once the work order exists. Only VGS enters work orders, so that no request exists outside the system.',
        },
        {
          kind: 'paragraph',
          text: 'On submission the platform issues a permanent work order number of the form RE-<facility>-<sequence>, records the date the request was received, and notifies VGS. The work order enters the status Submitted.',
        },
        {
          kind: 'table',
          caption: 'Priorities and what they signify',
          headers: ['Priority', 'Use for'],
          rows: [
            ['Emergency', 'Life-safety, security, or an outage stopping mail operations.'],
            ['Urgent', 'Significant impairment that will worsen or disrupt operations if left.'],
            ['Routine', 'Ordinary repair with no immediate operational impact.'],
            [
              'Scheduled',
              'Work to be performed at an agreed future date or during a planned window.',
            ],
          ],
        },
        {
          kind: 'paragraph',
          text: 'Each priority carries a response target configured for the task order, against which acknowledgement is measured.',
        },
      ],
    },
    {
      id: 'assessment',
      title: '4. Stage 2 — Acknowledgement and investigation',
      blocks: [
        {
          kind: 'steps',
          items: [
            'VGS acknowledges the request by moving it to Under VGS Review, which is visible to the Postal Service immediately.',
            'VGS contacts the site point of contact to arrange access and any escort or working-hours constraints.',
            'The work order moves to Estimate in Progress and VGS records the investigation: the date inspected, the findings, the root cause, and the recommended action, together with photographs.',
            'Where competition applies, VGS seeks quotes — not fewer than three, save by exception as described in section 6.',
          ],
        },
        {
          kind: 'paragraph',
          text: 'The findings, root cause, and recommended action are visible to the Postal Service as soon as they are recorded; there is no waiting for the proposal to learn what was found.',
        },
      ],
    },
    {
      id: 'proposal',
      title: '5. Stage 3 — Proposal',
      blocks: [
        {
          kind: 'paragraph',
          text: 'VGS prepares a priced proposal setting out the scope of work, a summary of the findings, and the itemized price. Subcontractor quotes supporting a subcontracted line are published with the proposal, so the Postal Service reviews the quote alongside the price it is being asked to authorize.',
        },
        {
          kind: 'paragraph',
          text: 'On submission the proposal version is frozen, the work order moves to Awaiting USPS Approval, and the approvers holding authority for that value are notified by email and in the portal. A frozen version is never amended: any change produces a new version, and the earlier version is retained in full.',
        },
      ],
    },
    {
      id: 'preconditions',
      title: '6. Stage 4 — Pre-authorization requirements',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Two obligations are enforced by the platform before a proposal can be authorized, rather than being left to the memory of whoever is handling the work order.',
        },
        {
          kind: 'definitions',
          items: [
            {
              term: 'Facilities Response Line determination',
              description:
                'For repairs above the referral threshold — $10,000 under the current configuration — the USPS Facilities Response Line determines whether the work qualifies as a project. The ticket number, the date of the call, and the outcome are recorded on the work order. Where the Response Line declines the work, the Contracting Officer may nevertheless authorize it where that serves the best interests of the Postal Service; that discretion is recorded as a distinct act, attributed and time-stamped, so “declined and authorized anyway” can never be confused with “never referred”.',
            },
            {
              term: 'Competition',
              description:
                'Three quotes is the floor where competition applies. Proceeding with fewer requires an exception: VGS records the number of quotes obtained and the justification, and the Contracting Officer approves or rejects it in the platform. Only one exception request may be open on a work order at a time. The Contracting Officer may also require competition on a work order that would not otherwise call for it.',
            },
          ],
        },
      ],
    },
    {
      id: 'authorization',
      title: '7. Stage 5 — Authorization',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Authority is tested against the total price to the Postal Service — the amount being obligated, and the amount that appears on the invoice.',
        },
        {
          kind: 'table',
          headers: ['Total price to USPS', 'Authority required', 'Additional requirement'],
          rows: [
            ['Up to $5,000', 'COR or Contracting Officer', 'None.'],
            [
              'Over $5,000',
              'COR review, then Contracting Officer',
              'The COR reviews and endorses first; the Contracting Officer gives the final approval. VGS is notified only after the CO approves.',
            ],
            [
              'Over $10,000',
              'Contracting Officer',
              'Facilities Response Line determination required first.',
            ],
          ],
        },
        {
          kind: 'paragraph',
          text: 'The approver may approve, reject, or request a revision. A rejection or revision request requires a reason, which is returned to VGS and retained on the record.',
        },
        {
          kind: 'note',
          variant: 'warning',
          title: 'Revisions are re-routed on value',
          text: 'A revised proposal is assessed afresh. A proposal first submitted at $4,800 and revised to $5,400 requires Contracting Officer authorization; it does not proceed on the COR authority that applied to the earlier version. An attempted approval beyond an approver’s authority is refused at the point of decision.',
        },
        {
          kind: 'paragraph',
          text: 'Approval thresholds are held as configuration with effective dates, not written into the software. A contract modification is applied by changing the figures, and each approval permanently stores the authority that applied on the day it was given, so historical records remain accurate after a change.',
        },
      ],
    },
    {
      id: 'execution',
      title: '8. Stage 6 — Scheduling and performance',
      blocks: [
        {
          kind: 'steps',
          items: [
            'On authorization the work order moves to Approved and the notice-to-proceed date is recorded — the date shown as “date request approved” in reporting.',
            'VGS schedules the repair and publishes the date and time window to the Postal Service; the work order moves to Repair Scheduled.',
            'When the crew is on site the work order moves to Work in Progress and the actual start is captured as the performance start date.',
            'If progress stops — awaiting parts, access, or a decision — the work order is placed On Hold with a recorded reason, and returns to the stage it interrupted when the hold is released.',
          ],
        },
      ],
    },
    {
      id: 'completion',
      title: '9. Stage 7 — Completion and closeout',
      blocks: [
        {
          kind: 'paragraph',
          text: 'A work order cannot be marked Completed without completion documentation. VGS records what was performed, the completion date and time, any follow-up recommendation, and any exceptions; completion photographs are attached when USPS requests them. The platform rejects a completion time that is in the future, earlier than the start of work, or earlier than the request itself, so reported cycle times cannot be distorted.',
        },
        {
          kind: 'paragraph',
          text: 'The invoice number is recorded on the work order once invoicing is raised and is visible to the Postal Service. VGS then closes the work order. A closed work order remains fully visible and reportable; closure ends activity, it does not archive the record.',
        },
      ],
    },
    {
      id: 'exceptions',
      title: '10. Exception paths',
      blocks: [
        {
          kind: 'table',
          headers: ['Situation', 'How the platform handles it'],
          rows: [
            [
              'The Postal Service wants changes to the price or scope',
              'Request revision. The work order returns to Estimate in Progress; VGS submits a new version, which is authorized on its own value.',
            ],
            [
              'The proposal is declined',
              'Reject, with a reason. The work order ends as Rejected and no work is performed.',
            ],
            ['The request is withdrawn', 'Cancel, with a reason. The record is retained.'],
            [
              'Work is blocked',
              'On Hold, with a reason; release returns the work order to the stage it interrupted.',
            ],
            [
              'The value exceeds the approver’s authority',
              'Refer to the Contracting Officer, or the approval is refused at the point of decision.',
            ],
            [
              'Fewer than three quotes are obtainable',
              'A competition exception is raised for Contracting Officer decision before authorization.',
            ],
            [
              'The Response Line declines work above the referral threshold',
              'The Contracting Officer may authorize at their discretion; the discretion is recorded separately from the approval.',
            ],
          ],
        },
      ],
    },
    {
      id: 'record',
      title: '11. The record and its integrity',
      blocks: [
        {
          kind: 'bullets',
          items: [
            'Status history is append-only, and only the transitions defined by the workflow are permitted. The rule is enforced twice: in the application and again in the database.',
            'Every proposal version is retained, including superseded ones, with the price, the scope, and the decision made on it.',
            'Every approval records the individual, the timestamp, the value, and the authority relied upon.',
            'The audit log records each action taken in the platform and cannot be edited or deleted.',
            'Documents are held in private storage and served only to users entitled to the work order; a link cannot be forwarded to someone outside its scope.',
            'Records are retained for six years in accordance with the retention requirement.',
          ],
        },
      ],
    },
    {
      id: 'reporting',
      title: '12. Reporting and data exchange',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The portal is the reporting system: it holds real-time data and is directly accessible to the Contracting Officer and the FSCMC. Reports are produced on demand for any period — monthly, quarterly, annual, or any other cadence the Contracting Officer determines — as a date-range filter over live data.',
        },
        {
          kind: 'paragraph',
          text: 'The work order report carries the information the agreement requires:',
        },
        {
          kind: 'bullets',
          items: [
            'Work Order Number',
            'Commodity or Service Provided',
            'Service Location — name, address, and Property ID',
            'Postal Service Area/Logistics',
            'Postal Service District/Division',
            'Work Description',
            'Local Facility Contact Information',
            'Date Request Received',
            'Date Request Approved — notice to proceed from the Contracting Officer',
            'Project Cost',
            'Performance Start Date',
            'Project/Service Status',
            'Invoice Number, where applicable',
            'Notes or Comments, including sourcing delays',
          ],
        },
        {
          kind: 'paragraph',
          text: 'Fields and formats are customizable: the Contracting Officer selects the columns required, in the order required, and exports them as CSV or consumes them as JSON, so a recurring extract can be defined once and repeated. An approvals report additionally provides one row per authorization — decider, date, value, and authority — as invoice backup. All reporting is scoped to the requesting user’s facilities and task orders.',
        },
      ],
    },
    {
      id: 'notifications',
      title: '13. Notifications',
      blocks: [
        {
          kind: 'paragraph',
          text: 'The platform notifies participants in the portal and by email at the points where a party has to act or has a right to know: a work order is submitted, a proposal is awaiting approval, and an approval decision has been made. Mail is queued and retried independently of the action that raised it, so a mail failure can never lose or reverse a work order action.',
        },
      ],
    },
    {
      id: 'summary',
      title: '14. Process summary',
      blocks: [
        {
          kind: 'table',
          headers: ['Stage', 'Status in the portal', 'Responsible'],
          rows: [
            ['Request raised', 'Submitted', 'Postal Service'],
            ['Acknowledged', 'Under VGS Review', 'VGS'],
            ['Investigated and priced', 'Estimate in Progress', 'VGS'],
            ['Proposal issued', 'Awaiting USPS Approval', 'Postal Service (COR or CO)'],
            ['Authorized', 'Approved', 'Postal Service'],
            ['Scheduled', 'Repair Scheduled', 'VGS'],
            ['Performed', 'Work in Progress', 'VGS'],
            ['Documented', 'Completed', 'VGS'],
            ['Finalized', 'Closed', 'VGS'],
          ],
        },
        {
          kind: 'paragraph',
          text: 'Exception states — Revision Requested, Rejected, On Hold, and Cancelled — branch from the path above and are described in section 10.',
        },
      ],
    },
  ],
};
